import { formatShortMoney } from "@/lib/formatters";
import { useRef } from "react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Upload } from "lucide-react";
import { Pill } from "@/components/FilterPills";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

export function PipelineHeader({
  searchQuery,
  onSearchChange,
  onImportExcel,
  projectCount,
  activeCount,
  importing,
  viewMode,
  onViewModeChange,
  archivedCount,
  sumTotalSales,
  sumForecasted,
  amFilter,
  onAmFilterChange,
  statusFilter,
  onStatusFilterChange,
  allAms = [],
  allStatuses = [],
}) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onImportExcel(file);
      e.target.value = "";
    }
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="pipeline-toolbar">
      <SearchBar value={searchQuery} onChange={onSearchChange}
                 placeholder="Search..." testId="search-input" />
      <Segmented
        options={[{ key: "pipeline", label: "Active",
                    count: activeCount || undefined, testId: "pipeline-view-active" },
                  { key: "archive", label: "Archived",
                    count: archivedCount || undefined, testId: "pipeline-view-archive" }]}
        value={viewMode}
        onChange={onViewModeChange}
      >
        <span className="mx-0.5 h-4 w-px bg-slate-300" />
        <Pill p={{ key: "pipeline-am", label: "AM", options: allAms.map((a) => ({ value: a, label: a })),
                   value: amFilter === "ALL" ? "" : amFilter, onChange: (v) => onAmFilterChange(v || "ALL") }} />
        <Pill p={{ key: "pipeline-status", label: "Status", multi: true, options: allStatuses.map((s) => ({ value: s, label: s })),
                   value: statusFilter.join("|"), onChange: (v) => onStatusFilterChange(v ? v.split("|") : []) }} />
      </Segmented>
      {viewMode === "pipeline" && (
        <>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                 onChange={handleFileSelect} data-testid="excel-file-input" />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid="import-excel-button" variant="outline" size="sm"
                        className="h-7 gap-1.5 px-2.5 text-xs bg-[#007BFF] hover:bg-[#0069d9] text-white border-transparent hover:text-white"
                        onClick={() => fileInputRef.current?.click()} disabled={importing}>
                  <Upload className="h-3.5 w-3.5" />
                  {importing ? "Importing..." : "Excel"}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Import projects from Excel (Bid Submitted + Won/Lost to archive)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}

      <div className="ml-auto flex items-center gap-2 text-xs">
        <span className="text-slate-400 font-medium" data-testid="pipeline-project-count">
          {projectCount} project{projectCount !== 1 ? "s" : ""}
        </span>
        <span className="font-semibold text-blue-900" data-testid="pipeline-total-sales">
          Total: {formatShortMoney(sumTotalSales)}
        </span>
        <span className="text-slate-300">|</span>
        <span className="font-semibold text-green-800" data-testid="pipeline-forecast-total">
          Forecast: {formatShortMoney(sumForecasted)}
        </span>
        <span className="font-semibold text-green-800" data-testid="pipeline-hit-rate">
          {sumTotalSales ? Math.round(((sumForecasted || 0) / sumTotalSales) * 100) : 0}%
        </span>
      </div>
    </div>
  );
}
